import NextImage from 'next/image';
import { Image } from 'react-datocms';
import { Heading } from '@zopauk/web-ui';
import B2BIcon from '@/components/Modules/B2BIcon';
import type { StoryboardItemFragment } from '@/utils/dato/generated';

// Animated GIFs must bypass `responsiveImage` — its `auto: format` flattens them to a
// static first frame. Serve the raw asset via imgix, cropped to a square without changing
// the format so the animation is preserved. `next/image` with `unoptimized` passes this
// URL through untouched (same approach as FeaturedImageBand / Article / PostCard), rather
// than re-optimising it and losing the animation. We request a 640px (2x) source so it
// stays sharp on retina in the 320px box while still being resized down from the raw GIF.
const gifSrc = (url: string) => `${url}?fit=crop&ar=1:1&w=640&h=640`;

export const StoryboardItem = ({ description, itemTitle, image, icon }: StoryboardItemFragment) => {
  const isGif = image?.mimeType === 'image/gif';

  return (
    <div className="text-center mx-3 px-5">
      {isGif ? (
        <div className="relative w-full max-w-[320px] mx-auto mb-6 aspect-square overflow-hidden rounded-2xl">
          <NextImage
            alt={image.responsiveImage?.alt || image.title || itemTitle || ''}
            style={{ objectPosition: '50% 50%', objectFit: 'cover' }}
            src={gifSrc(image.url)}
            unoptimized
            fill
          />
        </div>
      ) : image?.responsiveImage ? (
        <div className="w-full max-w-[320px] mx-auto mb-6 aspect-square overflow-hidden rounded-2xl">
          <Image
            style={{ objectFit: 'cover', height: '100%', width: '100%' }}
            data={image.responsiveImage}
            objectPosition="50% 50%"
          />
        </div>
      ) : null}

      {icon ? <B2BIcon className="mb-5" icon={icon} /> : null}

      {itemTitle ? (
        <Heading className="strip-title mb-3" size="h5" as="h3">
          {itemTitle}
        </Heading>
      ) : null}

      {description ? <p className="strip-description text-sm">{description}</p> : null}
    </div>
  );
};
